'use client'

import React, { useState } from 'react'
import type { ReactNode } from 'react'
import { Sheet, SheetClose, SheetContent, SheetTitle } from './sheet'
import { NavbarItem } from './navbar'

function CloseMenuIcon() {
  return (
    <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  )
}

interface MobileSidebarProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}

function MobileSidebar({ open, onOpenChange, children }: MobileSidebarProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="dashboard-mobile-panel w-full max-w-80 border-none bg-transparent p-1.5 shadow-none sm:p-2 lg:hidden">
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <div
          className="dashboard-mobile-sidebar-shell flex h-full flex-col overflow-hidden rounded-lg bg-white"
          onClickCapture={(event) => {
            const target = event.target as HTMLElement
            if (target.closest('a,button,[role="button"]')) {
              onOpenChange(false)
            }
          }}
        >
          <div className="dashboard-mobile-sidebar-close px-2 pt-2">
            <SheetClose asChild>
              <NavbarItem aria-label="Close navigation">
                <CloseMenuIcon />
              </NavbarItem>
            </SheetClose>
          </div>
          {children}
        </div>
      </SheetContent>
    </Sheet>
  )
}

interface SidebarLayoutProps {
  navbar: ReactNode | ((controls: { openSidebar: () => void }) => ReactNode)
  sidebar: ReactNode
  children: ReactNode
}

export function SidebarLayout({ navbar, sidebar, children }: SidebarLayoutProps) {
  const [showSidebar, setShowSidebar] = useState(false)
  const openSidebar = () => setShowSidebar(true)
  const renderedNavbar = typeof navbar === 'function' ? navbar({ openSidebar }) : navbar

  return (
    <div className="dashboard-shell-layout relative isolate flex min-h-svh w-full bg-transparent max-lg:flex-col lg:h-svh lg:min-h-0 lg:overflow-hidden lg:bg-transparent">
      <div className="dashboard-sidebar-rail hidden lg:flex lg:h-full lg:min-h-0 lg:shrink-0 lg:py-2 lg:pl-2">
        <div className="dashboard-sidebar-shell flex h-full min-h-0 overflow-hidden rounded-2xl bg-transparent lg:w-64">{sidebar}</div>
      </div>

      <MobileSidebar open={showSidebar} onOpenChange={setShowSidebar}>
        {sidebar}
      </MobileSidebar>

      <header className="dashboard-mobile-header flex items-center px-3 py-1.5 sm:px-4 lg:hidden">
        <div className="dashboard-mobile-navbar min-w-0 flex-1">{renderedNavbar}</div>
      </header>

      <main className="dashboard-main-scroll flex min-h-0 flex-1 flex-col pb-1 lg:min-w-0 lg:overflow-y-auto lg:pt-2 lg:pr-2 lg:pl-2">
        <div className="grow p-3 sm:p-4 lg:p-10">
          <div className="mx-auto max-w-[56rem]">{children}</div>
        </div>
      </main>
    </div>
  )
}
